import "./AddToListDropdown.scss";

import React, { useEffect, useState } from "react";
import { OverlayTrigger, MenuItem, Tooltip, Button, Popover } from "react-bootstrap";
import { FormattedMessage } from "react-intl";
import { withReactAdapter } from "../../module-federation/withReactAdapter";
import { useToggle } from "../../hooks/useToggle";
import { ShoppingList } from "../../types/ShoppingList";
import AddToListCreateModal from "./AddToListCreateModal";
import { useIsOnShoppingList } from "../../hooks/useIsOnShoppingList";
import AddToListHeartLarge from "../../assets/images/add-to-list-heart-large.svg";
import AddToListHeartFilledLarge from "../../assets/images/add-to-list-heart-filled-large.svg";
import AddToListHeartMedium from "../../assets/images/add-to-list-heart-medium.svg";
import AddToListHeartFilledMedium from "../../assets/images/add-to-list-heart-filled-medium.svg";
import AddToListHeartSmall from "../../assets/images/add-to-list-heart-small.svg";
import AddToListHeartFilledSmall from "../../assets/images/add-to-list-heart-filled-small.svg";
import { CustomerTier } from "../../types/ArticleCardProps";
import { AddToListConfig } from "../../types/AddToList";
import { connect } from "../../stores/storeConnect";
import ShoppingListStore from "../../stores/ShoppingListStore";
import ListAction from "../../actions/ListAction";
import {
    trackAddItemToListGA,
    trackAddItemToListNew,
    trackCreateListNew,
    trackDeleteItem
} from "../../tracking/ListTrackingUtil";
import { CompulsoryTrackingTags } from "../../tracking/Tracking";
import { PageType } from "../../types/PageType";
import ShoppingListActions from "../../actions/ShoppingListActions";
import { jsxWithIntl } from "../../i18n/IntlRootMixin";
import { BasketAddTrackingTags } from "../../tracking/BasketTrackingUtil";

type Size = "small" | "medium" | "large";

type AddToListButtonLabelProps = {
    labelText: React.JSX.Element | null;
    fontSize: Size | undefined;
};

function AddToListButtonLabel({ labelText, fontSize }: AddToListButtonLabelProps) {
    if (!labelText) {
        return null;
    }
    const sizeStyle: Size = fontSize ? fontSize : "medium";
    return <span className={"add-to-list-button-label " + sizeStyle}>{labelText}</span>;
}

type AddToListIconProps = {
    isOnShoppingList: boolean;
    iconSize: Size | undefined;
};

function AddToListIcon({ isOnShoppingList, iconSize }: AddToListIconProps) {
    switch (iconSize) {
        case "large":
            return isOnShoppingList ? <AddToListHeartFilledLarge /> : <AddToListHeartLarge />;
        case "medium":
            return isOnShoppingList ? <AddToListHeartFilledMedium /> : <AddToListHeartMedium />;
        case "small":
            return isOnShoppingList ? <AddToListHeartFilledSmall /> : <AddToListHeartSmall />;
        default:
            return isOnShoppingList ? <AddToListHeartFilledLarge /> : <AddToListHeartLarge />;
    }
}

type RenderedMenuItemProps = {
    bettyBundleId: string | null | undefined;
    list: ShoppingList;
    onMenuItemSelect: (list: ShoppingList, event?: React.SyntheticEvent<any> | undefined) => void;
    isCustomer: boolean;
    isEmployee: boolean;
};

function RenderedMenuItem({ bettyBundleId, list, onMenuItemSelect, isCustomer, isEmployee }: RenderedMenuItemProps) {
    const [isCustomerAndCannotEditList, setIsCustomerAndCannotEditList] = useState(list.readOnly && isCustomer);
    const [isEmployeeAndCannotEditList, setIsEmployeeAndCannotEditList] = useState(
        list.readOnlyForEmployees && isEmployee
    );

    useEffect(() => {
        setIsCustomerAndCannotEditList(list.readOnly && isCustomer);
    }, [list.readOnly, isCustomer]);

    useEffect(() => {
        setIsEmployeeAndCannotEditList(list.readOnlyForEmployees && isEmployee);
    }, [list.readOnlyForEmployees, isEmployee]);

    if (isCustomerAndCannotEditList || isEmployeeAndCannotEditList) {
        return null;
    }

    const alreadyOnList = list?.items?.find((bundle) => bundle.bettyBundleId === bettyBundleId);
    return (
        <MenuItem
            key={list.listId}
            onSelect={(eventkey: any, event?: any) => (bettyBundleId ? onMenuItemSelect(list, event) : {})}>
            {list.name}
            {alreadyOnList && (
                <span className="round-icon background-brand">
                    <i className="icon-check" />
                </span>
            )}
        </MenuItem>
    );
}

function addToListIsDisabled(isLoggedIn: boolean, isReplacementSearch: boolean, nonExistentBundle: boolean) {
    return !isLoggedIn || isReplacementSearch || nonExistentBundle;
}
function addToListIsShown(isLoggedIn: boolean, customerTier: CustomerTier | null | undefined) {
    return isLoggedIn && !(customerTier === "1" || customerTier === "2");
}

export type AddToListDropdownDisplayConfig = {
    iconSize?: Size;
    fontSize?: Size;
    buttonHeight?: Size;
    positionAbsolute?: boolean;
    showIconWithLabel?: boolean;
};

const defaultAddToListDropdownDisplayConfig: AddToListDropdownDisplayConfig = {
    iconSize: "large",
    fontSize: "large",
    buttonHeight: "large",
    positionAbsolute: true,
    showIconWithLabel: false
};

type Props = {
    onCreateNewListCallback?: () => void;
    bettyBundleId: string | null | undefined;
    bettyVariantId: string | null | undefined;
    articleDeprecated: boolean;
    onAddToListCallback: () => void;
    onRemoveFromListCallback: () => void;
    selectedDeliveryAddress: {
        [x: string]: any;
    };
    isLoggedIn: boolean;
    isCustomer: boolean;
    isEmployee: boolean;
    customerTier: CustomerTier | null | undefined;
    isReplacementSearch: boolean;
    nonExistentBundle: boolean;
    addToListDefaultGloballyVisible: boolean;
    shoppingLists: ShoppingList[];
    displayConfig?: AddToListDropdownDisplayConfig;
    compulsoryTrackingTags: CompulsoryTrackingTags;
    optionalTrackingTags?: BasketAddTrackingTags;
    container?: any;
};

type State = {
    shown: boolean;
    disabled: boolean;
    overlayClassName: string;
    bettyBundleId: string;
    bettyVariantId: string;
    articleDeprecated: boolean;
    selectedDeliveryAddress: {
        [x: string]: any;
    };
    compulsoryTrackingTags: CompulsoryTrackingTags;
};

export function AddToListDropdownUnconnected({
    onCreateNewListCallback,
    bettyBundleId,
    bettyVariantId,
    articleDeprecated,
    onAddToListCallback,
    onRemoveFromListCallback,
    selectedDeliveryAddress,
    isLoggedIn,
    isCustomer,
    isEmployee,
    customerTier,
    isReplacementSearch,
    nonExistentBundle,
    addToListDefaultGloballyVisible,
    shoppingLists,
    displayConfig = defaultAddToListDropdownDisplayConfig,
    compulsoryTrackingTags,
    optionalTrackingTags,
    container
}: Props) {
    const { value: showModal, setToggleValue: setShowModal } = useToggle(false);
    const { isOnShoppingList } = useIsOnShoppingList(bettyBundleId, shoppingLists);

    const [listConfig, setListConfig] = useState({
        isLoggedIn: isLoggedIn,
        isCustomer: isCustomer,
        isEmployee: isEmployee,
        customerTier: customerTier,
        isReplacementSearch: isReplacementSearch,
        nonExistentBundle: nonExistentBundle,
        addToListDefaultGloballyVisible: addToListDefaultGloballyVisible
    } as AddToListConfig);

    useEffect(() => {
        setListConfig({
            isLoggedIn: isLoggedIn,
            isCustomer: isCustomer,
            isEmployee: isEmployee,
            customerTier: customerTier,
            isReplacementSearch: isReplacementSearch,
            nonExistentBundle: nonExistentBundle,
            addToListDefaultGloballyVisible: addToListDefaultGloballyVisible
        });
    }, [
        isLoggedIn,
        isCustomer,
        isEmployee,
        customerTier,
        isReplacementSearch,
        nonExistentBundle,
        addToListDefaultGloballyVisible
    ]);

    const computeState = (
        listConfig: AddToListConfig,
        bettyBundleId: string | null | undefined,
        bettyVariantId: string | null | undefined,
        articleDeprecated: boolean,
        selectedDeliveryAddress: {
            [x: string]: any;
        },
        compulsoryTrackingTags: CompulsoryTrackingTags
    ) => {
        const shown = addToListIsShown(listConfig.isLoggedIn, listConfig.customerTier);
        const disableAddToList = addToListIsDisabled(
            listConfig.isLoggedIn,
            listConfig.isReplacementSearch,
            listConfig.nonExistentBundle
        );
        const disabled = disableAddToList || articleDeprecated;

        return {
            shown: shown,
            disabled: disabled,
            overlayClassName: disabled ? "pointer-events-none " : " ",
            bettyBundleId: bettyBundleId || "",
            bettyVariantId: bettyVariantId || "",
            articleDeprecated: articleDeprecated,
            selectedDeliveryAddress: selectedDeliveryAddress,
            compulsoryTrackingTags: {
                originEventId: compulsoryTrackingTags?.originEventId || "",
                pageType: compulsoryTrackingTags?.pageType || ("" as PageType)
            } as CompulsoryTrackingTags
        } as State;
    };

    const [state, setState] = useState(
        computeState(
            listConfig,
            bettyBundleId,
            bettyVariantId,
            articleDeprecated,
            selectedDeliveryAddress,
            compulsoryTrackingTags
        )
    );

    useEffect(() => {
        setState(
            computeState(
                listConfig,
                bettyBundleId,
                bettyVariantId,
                articleDeprecated,
                selectedDeliveryAddress,
                compulsoryTrackingTags
            )
        );
    }, [
        listConfig,
        bettyBundleId,
        bettyVariantId,
        articleDeprecated,
        selectedDeliveryAddress,
        shoppingLists,
        compulsoryTrackingTags?.originEventId,
        compulsoryTrackingTags?.pageType
    ]);

    const openCreateModal = () => {
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
    };

    const onCreateNewList = (listName: string, globallyVisible: boolean, readOnlyForEmployees: boolean) => {
        const newListItemData = {
            articleVariantId: state.bettyVariantId,
            bettyBundleId: state.bettyBundleId
        };
        ListAction.create(listName, globallyVisible, [newListItemData], readOnlyForEmployees);

        trackCreateListNew(
            state.bettyBundleId,
            state.bettyVariantId,
            listName,
            globallyVisible,
            readOnlyForEmployees,
            state.compulsoryTrackingTags,
            optionalTrackingTags
        );

        trackAddItemToListNew(
            state.bettyBundleId,
            state.bettyVariantId,
            "unknown",
            "unknown",
            listName,
            state.compulsoryTrackingTags,
            optionalTrackingTags
        );

        trackAddItemToListGA(state.bettyBundleId);

        if (onCreateNewListCallback) {
            onCreateNewListCallback();
        }
    };

    const onShoppingListSelect = (list: ShoppingList, event?: React.SyntheticEvent<any> | undefined) => {
        const itemIds = list?.items?.map((item) => item?.bettyBundleId);
        const containsItem = itemIds?.includes(state.bettyBundleId);
        const newListItemData = {
            articleVariantId: state.bettyVariantId,
            bettyBundleId: state.bettyBundleId
        };

        if (containsItem) {
            ShoppingListActions.deleteItem(newListItemData, {
                listId: list?.listId,
                listType: list?.type,
                listName: list?.name
            });

            trackDeleteItem(
                state.bettyBundleId,
                state.bettyVariantId,
                list?.type || "unknown",
                list?.listId || "",
                list?.name || "unknown",
                state.compulsoryTrackingTags,
                optionalTrackingTags
            );

            (event?.target as any).blur();

            if (onRemoveFromListCallback) {
                onRemoveFromListCallback();
            }
        } else {
            ShoppingListActions.addItem(list, state.bettyVariantId, state.bettyBundleId);

            trackAddItemToListNew(
                state.bettyBundleId,
                state.bettyVariantId,
                list?.type || "unknown",
                list?.listId || "",
                list?.name || "unknown",
                state.compulsoryTrackingTags,
                optionalTrackingTags
            );

            trackAddItemToListGA(state.bettyVariantId);

            (event?.target as any).blur();

            if (onAddToListCallback) {
                onAddToListCallback();
            }
        }
    };

    const InformativeMessage = () => {
        if (state.articleDeprecated) {
            return <FormattedMessage id="EX_CANNOT_ADD_ITEM_TO_LIST_TOOLTIP_DEPRECATED" />;
        }
        if (state.disabled) {
            return <FormattedMessage id="EX_CANNOT_ADD_ITEM_TO_LIST_TOOLTIP" />;
        }
        if (isOnShoppingList) {
            return <FormattedMessage id="SD_IS_ON_LIST_TOOLTIP" />;
        }
        return <FormattedMessage id="EX_ADD_ITEM_TO_LIST_TOOLTIP" />;
    };

    // Note: We want to have the informative message either in a tooltip or alternatively as a label,
    //   but not both at the same time
    const tooltip = !displayConfig?.showIconWithLabel ? (
        <Tooltip id={"idEXADDITEMTOLISTTOOLTIPForArticle" + (state.bettyBundleId || "")}>
            {<InformativeMessage />}
        </Tooltip>
    ) : (
        <></>
    );

    const buttonLabelText = displayConfig?.showIconWithLabel ? <InformativeMessage /> : null;

    const menu = (
        <Popover
            className="sd-shared--secondary-actions-addtolist-menu"
            id={"ex-add-to-list-popover-" + state.bettyBundleId}>
            <ul>
                <MenuItem className="sd-shared--create-modal-in-dropdown with-icon" onClick={openCreateModal}>
                    <FormattedMessage id="ORC_CREATE_NEW_LIST" />
                    <i className="icon-plus text-primary" />
                </MenuItem>
                {shoppingLists.length > 0 && <MenuItem divider />}
                {shoppingLists.map((list: ShoppingList) => (
                    <RenderedMenuItem
                        bettyBundleId={state.bettyBundleId}
                        list={list}
                        onMenuItemSelect={onShoppingListSelect}
                        isCustomer={listConfig.isCustomer}
                        isEmployee={listConfig.isEmployee}
                    />
                ))}
            </ul>
        </Popover>
    );

    if (!state.shown) return null;

    let classNameExtensions = " ";
    if (displayConfig?.positionAbsolute) {
        classNameExtensions += "position-absolute ";
    }

    const sizingEnsured = { ...defaultAddToListDropdownDisplayConfig, ...displayConfig };

    return (
        <>
            {jsxWithIntl(
                <div className={"sd-shared--add-to-list-indicator-button" + classNameExtensions}>
                    {showModal && (
                        <AddToListCreateModal
                            createNewList={onCreateNewList}
                            onClose={closeModal}
                            show
                            globallyVisible={listConfig.addToListDefaultGloballyVisible}
                            selectedDeliveryAddress={state.selectedDeliveryAddress}
                            isCustomer={listConfig.isCustomer}
                        />
                    )}
                    <OverlayTrigger
                        placement="bottom"
                        overlay={menu}
                        trigger={state.disabled ? [] : "click"}
                        container={container}
                        rootClose>
                        <OverlayTrigger placement="top" overlay={tooltip}>
                            <Button
                                bsStyle="link"
                                disabled={state.disabled}
                                className={state.overlayClassName + sizingEnsured?.buttonHeight}>
                                <AddToListIcon isOnShoppingList={isOnShoppingList} iconSize={sizingEnsured?.iconSize} />
                                <AddToListButtonLabel labelText={buttonLabelText} fontSize={sizingEnsured?.fontSize} />
                            </Button>
                        </OverlayTrigger>
                    </OverlayTrigger>
                </div>
            )}
        </>
    );
}

export const AddToListDropdown = connect(
    {
        stores: [
            [
                ShoppingListStore,
                (s: typeof ShoppingListStore) => ({ shoppingLists: s.getAllListsWithoutDynamicLists() })
            ]
        ]
    },
    AddToListDropdownUnconnected
);

export default withReactAdapter(AddToListDropdown);
